import { useEffect, useRef, useState } from 'react'
import { motion, useReducedMotion } from 'motion/react'
import { Sparkle } from 'lucide-react'
import { FormField } from './FormField'
import { inputClassName } from './fieldStyles'

// Duration of the `af-shimmer-sweep` keyframe in index.css. Mirrored here so the
// fill can be guaranteed to "complete" on a timer even when the animationend
// event never arrives (e.g. a backgrounded tab pauses CSS animations mid-fill).
const SHIMMER_DURATION_MS = 1500

export interface AutoFillFieldProps {
  label: string
  name: string
  value: string
  type?: string
  placeholder?: string
  helperText?: string
  error?: string
  /** Red border + aria-invalid without a per-field message (the section carries
   *  its own combined error message). */
  invalid?: boolean
  required?: boolean
  /** MAF 29-05: locked until the mandatory document is processed. */
  disabled?: boolean
  isAutoFilled?: boolean
  triggerShimmer?: boolean
  shimmerDelay?: number
  /** MAF 26-05 (trial): AI confidence 0..1, hidden once the user edits. */
  confidence?: number
  onChange?: (val: string) => void
}

export function AutoFillField({
  label,
  name,
  value,
  type = 'text',
  placeholder,
  helperText,
  error,
  invalid = false,
  required,
  disabled = false,
  isAutoFilled = false,
  triggerShimmer = false,
  shimmerDelay = 0,
  confidence,
  onChange,
}: AutoFillFieldProps) {
  const prefersReducedMotion = useReducedMotion()
  const [shimmerActive, setShimmerActive] = useState(false)
  const [shimmerDone, setShimmerDone] = useState(false)
  // MAF 26-05: once the user edits, the value is no longer the AI value, so the
  // AI markers (badge) and confidence line fade away.
  const [edited, setEdited] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // When the field stops being AI-filled (document removed or replaced), forget
  // any prior manual edit so a fresh extraction re-fills cleanly and the badge
  // returns. Adjusted during render on the prop change — not in an effect.
  const [prevAutoFilled, setPrevAutoFilled] = useState(isAutoFilled)
  if (prevAutoFilled !== isAutoFilled) {
    setPrevAutoFilled(isAutoFilled)
    if (!isAutoFilled) setEdited(false)
  }

  // Determine whether the autofill visual state should be shown
  const showAutoFill = isAutoFilled && triggerShimmer

  // Reduced motion skips the animation and shows the badge immediately.
  const reducedMotionResolved = showAutoFill && !!prefersReducedMotion
  const badgeMounted = shimmerDone || reducedMotionResolved
  // The badge only belongs while the field is genuinely AI-filled and untouched.
  // When the source document is removed or replaced the parent clears
  // isAutoFilled, so the badge fades out instead of lingering over the now
  // empty / locked field.
  const badgeVisible = badgeMounted && !edited && isAutoFilled

  useEffect(() => {
    if (!showAutoFill || prefersReducedMotion) return

    timerRef.current = setTimeout(() => {
      setShimmerActive(true)
    }, shimmerDelay)

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [showAutoFill, shimmerDelay, prefersReducedMotion])

  // Once the sweep is on screen, guarantee it finishes — and the AI badge
  // appears — even if the `animationend` event never fires. CSS animations
  // pause when the tab is backgrounded (and never resume their event), so
  // without this fallback a field filled while the user was on another tab
  // would silently lose its badge. onAnimationEnd stays the primary, precise
  // trigger; whichever fires first wins (the other is cleared/ignored).
  useEffect(() => {
    if (!shimmerActive) return
    const t = setTimeout(() => {
      setShimmerActive(false)
      setShimmerDone(true)
    }, SHIMMER_DURATION_MS + 50)
    return () => clearTimeout(t)
  }, [shimmerActive])

  const handleShimmerComplete = () => {
    setShimmerActive(false)
    setShimmerDone(true)
  }

  // While the fill sweep plays, the field carries an active-blue stroke; the
  // moment it finishes the stroke eases back to the standard field border, so
  // a populated AI field looks like every other input (the badge + confidence
  // line carry the AI signal at rest).
  const inputStyle: React.CSSProperties = {
    transition: 'border-color 250ms ease-out',
    ...(shimmerActive
      ? { borderColor: 'var(--field-autofill-border)' }
      : error || invalid
        ? { borderColor: 'var(--color-error-border)' }
        : {}),
  }

  return (
    <FormField
      label={label}
      name={name}
      error={error}
      helperText={helperText}
      required={required}
      disabled={disabled}
      confidence={edited ? undefined : confidence}
    >
      {/* Outer wrapper is NOT clipped so the corner badge and the fill glow can
          extend past the field edge. */}
      <div
        style={{
          position: 'relative',
          borderRadius: 'var(--radius-md)',
          ...(shimmerActive ? { animation: 'af-stroke-glow 1.5s ease-in-out' } : {}),
        }}
      >
        {/* Clip layer: rounds the field and (only during the shimmer) clips the sweep. */}
        <div
          style={{
            position: 'relative',
            borderRadius: 'var(--radius-md)',
            overflow: shimmerActive ? 'hidden' : 'visible',
          }}
        >
          <input
            id={name}
            name={name}
            type={type}
            value={value}
            placeholder={placeholder}
            disabled={disabled}
            onChange={(e) => {
              if (!edited) setEdited(true)
              onChange?.(e.target.value)
            }}
            className={inputClassName}
            style={inputStyle}
            aria-invalid={!!error || invalid}
            aria-describedby={error ? `${name}-error` : undefined}
          />

          {/* Shimmer overlay — sweeps the brand --gradient-shimmer token once as the field fills */}
          {shimmerActive && !prefersReducedMotion && (
            <div
              aria-hidden="true"
              onAnimationEnd={(e) => {
                if (e.animationName === 'af-shimmer-sweep') handleShimmerComplete()
              }}
              style={{
                position: 'absolute',
                inset: 0,
                pointerEvents: 'none',
                borderRadius: 'var(--radius-md)',
                background: 'var(--gradient-shimmer)',
                backgroundSize: '220% 100%',
                backgroundRepeat: 'no-repeat',
                animation: 'af-shimmer-sweep 1.5s cubic-bezier(0.4, 0, 0.2, 1) 1 both',
              }}
            />
          )}
        </div>

        {/* AI badge — gradient pill with a single shimmering sparkle.
            Fades out (and lifts) once the user edits the value. */}
        {badgeMounted && (
          <div
            aria-label="Auto-filled by AI"
            style={{
              position: 'absolute',
              top: '-10px',
              right: '12px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--spacing-1)',
              background:
                'var(--gradient-action)',
              color: 'var(--field-autofill-badge-text)',
              borderRadius: 'var(--radius-full)',
              fontFamily: 'var(--font-body)',
              fontSize: '0.6875rem',
              fontWeight: 'var(--weight-medium)',
              letterSpacing: '0.02em',
              padding: '4px 9px',
              lineHeight: 1,
              // Fade + lift out via CSS transition once the user edits the value.
              opacity: badgeVisible ? 1 : 0,
              transform: badgeVisible
                ? 'translateY(0) scale(1)'
                : 'translateY(-5px) scale(0.85)',
              transition: 'opacity 250ms ease-out, transform 250ms ease-out',
              pointerEvents: 'none',
            }}
          >
            <motion.span
              style={{ display: 'inline-flex' }}
              animate={prefersReducedMotion ? undefined : { opacity: [0.4, 1, 0.4, 1] }}
              transition={
                prefersReducedMotion
                  ? undefined
                  : { duration: 2.4, ease: 'easeInOut', times: [0, 0.33, 0.66, 1] }
              }
            >
              <Sparkle size={13} fill="#ffffff" strokeWidth={1.5} />
            </motion.span>
            AI-filled
          </div>
        )}
      </div>
    </FormField>
  )
}
